import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useHistory } from 'react-router';
import CheckItem from '../../components/CheckItem/CheckItem';
import BulletList from '../../components/BulletList/BulletList';
import WebcamCapture from '../../components/WebcamCapture/WebcamCapture';
import useApplicationUid from '../../hooks/useApplicationUid';
import { socureUploadDocument } from '../../store/actions/documents/documentsActions';
import { PAGES } from '../../constants/pages';
import BaseLayout from '../../components/BaseLayout';
import Header from '../../components/Header';
import OnBoardingCta from '../../components/OnBoarding/OnBoardingCta';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE_GTM,
  KYC_SELECTED_OPTION,
} from '../../constants/sessionStorage';
import {
  KYC_DOCUMENT_TYPES,
  KYC_PERSON_TARGET_TYPES,
} from '../../util/constants';
import Paragraph from '../../components/Paragraph/Paragraph';
import themeColors from '../../assets/styles/themeColors';
import { separateByUppercaseAndJoinedWithHorLine } from '../../util/helpers/stringHelpers';
import { useApplicantData } from '../../hooks/useApplicantData';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';

const DropZoneWrapperSingle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const KycUploadIdentityDocumentsViaWebcam = ({
  applicantType,
  documentType,
}) => {
  const { ApplicationUid } = useApplicationUid();
  const ApplicantData = useApplicantData();
  const isPassport = documentType === 'Passport';
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const selectedDocument = retrieveFromSessionStorage(KYC_SELECTED_OPTION);
  const [errors, setErrors] = useState('');
  const [uploadedDocuments, setUploadedDocuments] = useState(
    isPassport
      ? { Document: '' }
      : {
          DocumentFront: '',
        },
  );

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Kyc camera',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Kyc camera',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };

  const onSubmit = () => {
    const formData = new FormData();
    if (isPassport) {
      formData.append('Document', uploadedDocuments.Document);
    } else {
      formData.append('DocumentFront', uploadedDocuments.DocumentFront);
    }
    formData.append(
      'Caption',
      KYC_DOCUMENT_TYPES[`${selectedDocument.DocumentType}`],
    );

    const onSuccess = () => {
      handleGtmEvent();
      history.push(PAGES.KYC_VERIFICATION);
    };
    const params = {
      personTargetType:
        KYC_PERSON_TARGET_TYPES[selectedDocument?.PersonTargetType],
      documentType: KYC_DOCUMENT_TYPES[`${selectedDocument?.DocumentType}`],
      socureDocumentType: selectedDocument.SocureDocumentType,
    };

    dispatch(
      socureUploadDocument({
        ApplicationUid,
        params,
        data: formData,
        onSuccess,
        setErrors,
      }),
    );
  };

  const getPreviousPath = () =>
    PAGES.KYC_PROOF_OF_IDENTITY[String(applicantType).toUpperCase()].UPLOAD[
      String(separateByUppercaseAndJoinedWithHorLine(documentType))
    ];

  return (
    <BaseLayout previousPath={getPreviousPath()} isKycProcess="true" noLogoBox>
      <Header
        titleMb={32}
        title={
          isPassport
            ? t('kycVerification.proofOfIdentityTitleWebcamPassport')
            : t('kycVerification.proofOfIdentityTitleWebcam')
        }
      />
      <BulletList mb={24}>
        <CheckItem
          text={t('kycVerification.checkItemOne')}
          color={themeColors.colorTextLight}
        />
        <CheckItem
          text={t('kycVerification.checkItemTwo')}
          color={themeColors.colorTextLight}
        />
        <CheckItem
          text={t('kycVerification.checkItemThree')}
          color={themeColors.colorTextLight}
        />
      </BulletList>
      <DropZoneWrapperSingle>
        {isPassport ? (
          <WebcamCapture
            isPassport
            onCapture={(image) => {
              setUploadedDocuments({
                ...uploadedDocuments,
                Document: image,
              });
            }}
          />
        ) : (
          <WebcamCapture
            frontSide
            onCapture={(image) => {
              setUploadedDocuments({
                ...uploadedDocuments,
                DocumentFront: image,
              });
            }}
          />
        )}
      </DropZoneWrapperSingle>
      <Paragraph color={themeColors.colorErrorMessage} variant="normal">
        {errors}
      </Paragraph>
      <OnBoardingCta
        disabled={
          isPassport
            ? !uploadedDocuments.Document
            : !uploadedDocuments.DocumentFront
        }
        onClick={onSubmit}
      />
    </BaseLayout>
  );
};

KycUploadIdentityDocumentsViaWebcam.propTypes = {
  applicantType: PropTypes.string,
  documentType: PropTypes.string,
};

export default KycUploadIdentityDocumentsViaWebcam;
